import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import format from "lib/format";
import { getOtherCategories, applyFiltersToFullDataset } from "../functions";
import { fetchCatalogue } from "../endpoints";
import { CACHE_DURATION, CATALOGUE_PAGE_SIZE } from "../constants";
import { cacheService } from "./services/cacheService";

const DataContext = createContext();

/**
 * Custom hook to use the DataContext
 * @returns {Object} The data context value
 * @throws {Error} When used outside of a DataProvider
 */
export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataContext must be used within a DataProvider");
  }
  return context;
};

/**
 * DataProvider component that manages learning catalogue data
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.public - Whether to use public endpoint (default: false)
 * @returns {React.ReactElement} DataProvider component
 */
export const DataProvider = ({ children, public: isPublic = false }) => {
  const [data, setData] = useState({
    learningActivities: null,
    otherCategories: null,
    loading: false,
    error: null,
  });

  // Persist cache at module level so it survives navigation and reload (used within CACHE_DURATION)
  const [rawCatalogueData, setRawCatalogueDataState] = useState(() =>
    cacheService.getValidData(),
  );
  const pendingFetchRef = useRef(null);

  const isCacheValid = useCallback(() => cacheService.isValid(), []);

  const setRawCatalogueData = useCallback((data) => {
    cacheService.set(data);
    setRawCatalogueDataState(data);
  }, []);

  const getCachedData = useCallback(() => cacheService.getValidData(), []);

  /**
   * Unique regions and cities from the full catalogue (unfiltered).
   * Used so Session filter options (Område, Ort) stay visible when category filters are applied.
   */
  const allUniqueRegionsAndCities = useMemo(() => {
    const courses = rawCatalogueData || [];
    if (!courses.length) {
      return { allUniqueRegions: [], allUniqueCities: [] };
    }
    const regions = new Set();
    const cities = new Set();
    courses.forEach((course) => {
      if (course.sessions && Array.isArray(course.sessions)) {
        course.sessions.forEach((session) => {
          if (session.region) {
            regions.add(session.region);
          }
          if (session.city) {
            cities.add(session.city);
          }
        });
      }
    });
    return {
      allUniqueRegions: Array.from(regions).sort(),
      allUniqueCities: Array.from(cities).sort(),
    };
  }, [rawCatalogueData]);

  /**
   * Clears cache (storage and in-memory) so next fetch loads from endpoint
   */
  const clearCache = useCallback(() => {
    cacheService.clear();
    setRawCatalogueDataState(null);
  }, []);

  /**
   * Updates the data state with new values
   * @param {Object} updates - Object containing updates to apply to data state
   */
  const updateData = useCallback((updates) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Fetches other categories data from the API
   */
  const fetchOtherCategories = useCallback(async () => {
    try {
      const categories = await getOtherCategories(isPublic);
      updateData({ otherCategories: categories });
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  }, [updateData, isPublic]);

  /**
   * Fetches raw catalogue data from API
   * @returns {Promise<Array>} Promise resolving to array of courses
   */
  const fetchRawCatalogueData = useCallback(async () => {
    try {
      const response = await fetchCatalogue(isPublic);
      const formattedCourses = (response || []).map((c) => ({
        ...c,
        imageUrl: c?.imageUrl ? format.url(`/${c.imageUrl}`) : undefined,
      }));
      setRawCatalogueData(formattedCourses);
      return formattedCourses;
    } catch (error) {
      console.error("Failed to fetch raw catalogue data:", error);
      throw error;
    }
  }, [isPublic, setRawCatalogueData]);

  /**
   * Gets raw catalogue data, using cache if valid
   * @returns {Promise<Array>} Promise resolving to array of courses
   */
  const getRawCatalogueData = useCallback(async () => {
    const cached = getCachedData();
    if (cached) {
      return cached;
    }
    if (pendingFetchRef.current) {
      return pendingFetchRef.current;
    }
    const promise = fetchRawCatalogueData();
    pendingFetchRef.current = promise;
    try {
      const result = await promise;
      return result;
    } finally {
      pendingFetchRef.current = null;
    }
  }, [getCachedData, fetchRawCatalogueData]);

  /**
   * Converts array values to single values for backward compatibility with underlying functions
   * @param {number|Array<number>} value - Value that might be an array
   * @param {number} defaultValue - Default value if empty
   * @returns {number} Single number value
   */
  const toSingleValue = (value, defaultValue = 0) => {
    if (Array.isArray(value)) {
      return value;
    }
    return value ?? defaultValue;
  };

  /**
   * Converts array string values to single string for backward compatibility
   * @param {string|Array<string>} value - Value that might be an array
   * @param {string} defaultValue - Default value if empty
   * @returns {string} Single string value
   */
  const toSingleString = (value, defaultValue = "") => {
    if (Array.isArray(value)) {
      return value;
    }
    return value ?? defaultValue;
  };

  /**
   * Applies filters to existing data without API calls
   * If data doesn't exist, it will be fetched first
   * @param {import('./types/fetchOptions').FetchCatalogueDataOptions} options - Options object containing filter and pagination parameters
   * @param {Array|null} [dataOverride] - Optional raw data to filter (e.g. fresh fetch when bypassing cache)
   * @returns {Promise<Array>} Promise resolving to array of paginated courses
   */
  const applyFiltersToData = useCallback(
    async (options = {}, dataOverride = null) => {
      let dataToFilter = dataOverride ?? rawCatalogueData;

      if (!dataToFilter) {
        dataToFilter = await getRawCatalogueData();
      }

      if (!dataToFilter || dataToFilter.length === 0) {
        console.warn("No raw data available for filtering");
        updateData({
          learningActivities: {
            totalAmountOfCourses: 0,
            courses: [],
            filteredCourses: [],
            allFilteredCourses: [],
          },
          loading: false,
          error: null,
        });
        return [];
      }

      const {
        searchInput = "",
        category1 = 0,
        category2 = 0,
        category3 = 0,
        category4 = 0,
        category5 = 0,
        pageNum = 1,
        sortOrderParam = 0,
        startDate = null,
        endDate = null,
        sessionRegion = "",
        sessionCity = "",
      } = options;

      const cat1 = toSingleValue(category1, 0);
      const cat2 = toSingleValue(category2, 0);
      const cat3 = toSingleValue(category3, 0);
      const cat4 = toSingleValue(category4, 0);
      const cat5 = toSingleValue(category5, 0);
      const region = toSingleString(sessionRegion, "");
      const city = toSingleString(sessionCity, "");

      const allFilteredCourses = applyFiltersToFullDataset(dataToFilter, {
        searchInput,
        category1: cat1,
        category2: cat2,
        category3: cat3,
        category4: cat4,
        category5: cat5,
        sortOrderParam,
        startDate,
        endDate,
        sessionRegion: region,
        sessionCity: city,
      });

      const startIndex = (pageNum - 1) * CATALOGUE_PAGE_SIZE;
      const endIndex = startIndex + CATALOGUE_PAGE_SIZE;
      const paginatedCourses = allFilteredCourses.slice(startIndex, endIndex);

      updateData({
        learningActivities: {
          totalAmountOfCourses: allFilteredCourses.length,
          courses: paginatedCourses,
          filteredCourses: paginatedCourses,
          allFilteredCourses,
        },
        loading: false,
        error: null,
      });

      return paginatedCourses;
    },
    [
      rawCatalogueData,
      updateData,
      toSingleValue,
      toSingleString,
      getRawCatalogueData,
    ],
  );

  /**
   * Fetches catalogue data with filtering and pagination
   * Checks cache first and only fetches from API if needed
   * @param {import('./types/fetchOptions').FetchCatalogueDataOptions} options - Options object containing filter and pagination parameters
   * @param {boolean} forceRefresh - Force refresh from API even if cache is valid
   * @returns {Promise<Array>} Promise resolving to array of courses
   */
  const fetchCatalogueData = useCallback(
    async (options = {}, forceRefresh = false) => {
      try {
        updateData({ loading: true, error: null });

        if (!forceRefresh && isCacheValid() && rawCatalogueData) {
          const courses = await applyFiltersToData(options);
          return courses;
        }

        if (forceRefresh) {
          clearCache();
        }
        const freshData = await getRawCatalogueData();
        const courses = await applyFiltersToData(options, freshData);
        return courses;
      } catch (error) {
        console.error("Failed to fetch catalogue data:", error);
        updateData({ loading: false, error });
        return [];
      }
    },
    [
      updateData,
      isCacheValid,
      rawCatalogueData,
      applyFiltersToData,
      getRawCatalogueData,
      clearCache,
    ],
  );

  /**
   * Refreshes catalogue data from API, bypassing cache
   * @param {import('./types/fetchOptions').FetchCatalogueDataOptions} options - Options object containing filter and pagination parameters
   * @returns {Promise<Array>} Promise resolving to array of courses
   */
  const refreshCatalogueData = useCallback(
    async (options = {}) => fetchCatalogueData(options, true),
    [fetchCatalogueData],
  );

  const value = {
    data,
    ...allUniqueRegionsAndCities,
    fetchOtherCategories,
    fetchCatalogueData,
    refreshCatalogueData,
    applyFiltersToData,
    updateData,
    clearCache,
    isPublic,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

DataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  public: PropTypes.bool,
};
